import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiSend } from 'react-icons/fi';
import OnboardingChat from './OnboardingChat';
import PickerPopover from './PickerPopover';
import { useLanguage } from '../contexts/LanguageContext';
import locationOptions from '../locationOptions.json';
import '../../styles/jobseeker/scout-setup.css';

const US = 'United States';
const EMPTY = Object.freeze({});
const split = (value) =>
  Array.isArray(value)
    ? value
    : String(value || '')
        .split(/[;\n|]+/)
        .map((s) => s.trim())
        .filter(Boolean);
const option = (value, label = value) => ({ value, label });
const cityKey = (city) =>
  JSON.stringify({
    city: city.city,
    state: city.state || '',
    country: city.country,
  });
const countryCode = (name) =>
  locationOptions.countries.find((c) => c.name === name)?.code;
const normalize = (text) =>
  String(text)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f\u064b-\u065f\u0670\u0640]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .toLowerCase();

export function SearchPicker({
  label,
  options,
  values = [],
  onChange,
  multiple = true,
  allowCustom = false,
  disabled = false,
  placeholder,
  customLabel,
  removeLabel,
}) {
  const id = useId();
  const inputRef = useRef(null);
  const tagsRef = useRef(null);
  const { language } = useLanguage();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [resultLimit, setResultLimit] = useState(300);
  const selectedLabel =
    options.find((item) => item.value === values[0])?.label || values[0] || '';
  const previousCount = useRef(values.length);
  useEffect(() => {
    if (values.length > previousCount.current && tagsRef.current) {
      tagsRef.current.scrollLeft =
        (language === 'ar' ? -1 : 1) * tagsRef.current.scrollWidth;
    }
    previousCount.current = values.length;
  }, [values.length, language]);
  useEffect(() => {
    if (open)
      document
        .getElementById(`${id}-option-${active}`)
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [active, id, open, query]);
  const matching = options.filter(
    (item) =>
      (!multiple || !values.includes(item.value)) &&
      [item.label, item.value, ...(item.searchTerms || [])].some((text) =>
        normalize(text).includes(normalize(query)),
      ),
  );
  const available = matching.slice(0, resultLimit);
  if (
    allowCustom &&
    query.trim() &&
    !options.some(
      (item) => normalize(item.value) === normalize(query.trim()),
    ) &&
    !values.includes(query.trim())
  ) {
    available.push(
      option(query.trim(), `${customLabel || 'Use'} “${query.trim()}”`),
    );
  }
  const select = (value) => {
    onChange(multiple ? [...values, value] : [value]);
    setQuery('');
    setActive(0);
    setOpen(false);
  };
  return (
    <div className="scout-setup__picker">
      <label htmlFor={id}>
        <span>{label}</span>
        {multiple && (
          <span className="scout-setup__picker-count" aria-hidden="true">
            {values.length || ''}
          </span>
        )}
      </label>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-autocomplete="list"
        aria-describedby={multiple ? `${id}-selected` : undefined}
        aria-activedescendant={
          open && available[active] ? `${id}-option-${active}` : undefined
        }
        value={!multiple && !open ? selectedLabel : query}
        disabled={disabled}
        placeholder={!multiple && selectedLabel ? selectedLabel : placeholder}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setResultLimit(300);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setQuery('');
            setOpen(false);
            return;
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            if (open && e.key === 'ArrowDown' && active === available.length - 1 && available.length < matching.length) {
              setResultLimit((limit) => limit + 300);
              setActive((n) => n + 1);
              return;
            }
            setActive((n) =>
              !open
                ? 0
                : Math.max(
                    0,
                    Math.min(
                      available.length - 1,
                      n + (e.key === 'ArrowDown' ? 1 : -1),
                    ),
                  ),
            );
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            if (open && available[active]) select(available[active].value);
            else setOpen(true);
          }
        }}
      />
      {multiple && (
        <>
          <span
            id={`${id}-selected`}
            className="scout-setup__sr-only"
            aria-live="polite"
          >
            {language === 'ar'
              ? `${values.length} محدد`
              : `${values.length} selected`}
          </span>
          <div
            ref={tagsRef}
            className="scout-setup__selection"
            role="group"
            aria-label={
              language === 'ar' ? `${label} — المحدد` : `${label} — selected`
            }
          >
            {values.length === 0 && (
              <span className="scout-setup__empty-selection">
                {language === 'ar'
                  ? 'لم يتم الاختيار بعد'
                  : 'Nothing selected yet'}
              </span>
            )}
            {values.map((value) => {
              const name =
                options.find((o) => o.value === value)?.label || value;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={disabled}
                  className="scout-setup__chip"
                  title={name}
                  onClick={() => onChange(values.filter((v) => v !== value))}
                  aria-label={`${removeLabel || 'Remove'} ${name}`}
                >
                  <span className="scout-setup__chip-label">{name}</span>
                  <span className="scout-setup__chip-remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
      {open && !disabled && (
        <PickerPopover
          anchorRef={inputRef}
          onClose={() => {
            setOpen(false);
            setQuery('');
          }}
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          className="scout-setup__results"
          onScroll={(event) => {
            const menu = event.currentTarget;
            if (menu.scrollHeight - menu.scrollTop - menu.clientHeight < 44)
              setResultLimit((limit) => limit + 300);
          }}
        >
          {available.length === 0 && (
            <div role="status" className="scout-setup__no-results">
              {language === 'ar'
                ? 'لا توجد نتائج. جرّب اسماً آخر.'
                : 'No results. Try another name.'}
            </div>
          )}
          {available.map((item, i) => (
            <button
              id={`${id}-option-${i}`}
              key={item.value}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              className={i === active ? 'is-active' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(item.value)}
            >
              {item.label}
            </button>
          ))}
        </PickerPopover>
      )}
    </div>
  );
}

export function answersFromScout(scout = {}) {
  const saved = scout.onboardingAnswers || {};
  const loc = scout.locationPreferences || {};
  return {
    ...saved,
    targetDirection:
      saved.targetDirection || (scout.goals?.targetTitles || []).join('; '),
    seniorityPreference:
      saved.seniorityPreference || scout.goals?.seniority?.[0] || '',
    jobTypes: saved.jobTypes || scout.goals?.jobTypes || ['full_time'],
    workSetup: saved.workSetup || [
      ...(loc.remotePreference || []),
      ...(loc.relocationOpen ? ['open_to_relocation'] : []),
    ],
    residenceCountry: saved.residenceCountry || loc.residenceCountry || '',
    targetCountries:
      saved.targetCountries ||
      (loc.targetCountries || loc.countries || []).join('; '),
    targetStates: saved.targetStates ||
      loc.targetStates || [
        ...new Set(
          (loc.targetLocations || [])
            .filter((l) => l.country === US && l.state)
            .map((l) => l.state),
        ),
      ],
    targetCityEntries: (loc.targetLocations || []).filter((l) => l.city),
    workAuthorizationCountries:
      loc.workAuthorizationStatus === 'unknown'
        ? ''
        : saved.workAuthorizationCountries ||
          (loc.workAuthorizationCountries || []).join('; '),
    professionalLanguages:
      saved.professionalLanguages || (scout.cv?.languages || []).join('; '),
  };
}

export function setupAnswers(draft) {
  return {
    targetDirection: draft.roles.join('; '),
    seniorityPreference: draft.seniority,
    jobTypes: draft.jobTypes,
    workSetup: draft.workSetup,
    residenceCountry: draft.residence,
    targetCountries: draft.countries.join('; '),
    targetStates: draft.countries.includes(US) ? draft.states : [],
    targetCities:
      draft.cities
        .filter(
          (c) =>
            draft.countries.includes(c.country) &&
            (c.country !== US || draft.states.includes(c.state)),
        )
        .map((c) =>
          [c.city, c.country === US ? c.state : '', c.country]
            .filter(Boolean)
            .join(', '),
        )
        .join('; ') || 'Anywhere',
    workAuthorizationCountries: draft.authorization,
    professionalLanguages: draft.languages,
    salaryPreference: draft.salary || 'Flexible',
    dealbreakers: draft.dealbreakers || 'None',
  };
}

function SalaryPicker({ value, onChange, disabled, copy }) {
  const initial = String(value || '');
  const [amount, setAmount] = useState(() =>
    (initial.match(/\d[\d,]*(?:\.\d+)?/)?.[0] || '').replaceAll(',', ''),
  );
  const [currency, setCurrency] = useState(
    () => initial.match(/\b[A-Z]{3}\b/)?.[0] || 'USD',
  );
  const [strict, setStrict] = useState(() => /strict/i.test(initial));
  const update = (nextAmount, nextCurrency, nextStrict) => {
    if (nextAmount && Number(nextAmount) < 0) return;
    setAmount(nextAmount);
    setCurrency(nextCurrency);
    setStrict(nextStrict);
    onChange(
      Number(nextAmount) > 0
        ? `${nextAmount} ${nextCurrency}${nextStrict ? ' strict' : ''}`
        : 'Flexible',
    );
  };
  const currencies =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('currency')
      : ['USD', 'AED', 'EUR', 'GBP', 'SAR', 'CAD', 'LBP'];
  return (
    <fieldset className="scout-setup__choices">
      <legend>
        {copy(
          'Minimum annual base salary (optional)',
          'الحد الأدنى للراتب الأساسي السنوي (اختياري)',
        )}
      </legend>
      <div className="scout-setup__salary">
        <label>
          {copy('Amount per year', 'المبلغ السنوي')}
          <input
            type="number"
            min="0"
            inputMode="decimal"
            value={amount}
            placeholder={copy('Flexible', 'مرن')}
            disabled={disabled}
            onChange={(event) => update(event.target.value, currency, strict)}
          />
        </label>
        <label>
          {copy('Currency', 'العملة')}
          <select
            value={currency}
            disabled={disabled}
            onChange={(event) => update(amount, event.target.value, strict)}
          >
            {[...new Set([currency, ...currencies])].map((code) => (
              <option key={code}>{code}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="scout-setup__check">
        <input
          type="checkbox"
          checked={strict}
          disabled={disabled}
          onChange={(event) => update(amount, currency, event.target.checked)}
        />
        {copy(
          'Exclude jobs with a stated salary below this minimum',
          'استبعد الوظائف ذات الراتب المعلن الأقل من هذا الحد',
        )}
      </label>
    </fieldset>
  );
}

function Choices({
  label,
  options,
  values,
  onChange,
  disabled,
  dropdown = false,
}) {
  const id = useId();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { language } = useLanguage();
  const selected = options.filter((item) => values.includes(item.value));
  const content = (
    <fieldset className="scout-setup__choices">
      <legend>{label}</legend>
      <div>
        {options.map((item) => (
          <button
            type="button"
            key={item.value}
            disabled={disabled}
            aria-pressed={values.includes(item.value)}
            onClick={() =>
              onChange(
                values.includes(item.value)
                  ? values.filter((v) => v !== item.value)
                  : [...values, item.value],
              )
            }
          >
            {item.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
  if (!dropdown) return content;
  return (
    <div className="js-onboarding__choice-dropdown">
      <span id={`${id}-label`}>{label}</span>
      <button
        type="button"
        ref={anchorRef}
        className="scout-setup__dropdown-trigger"
        disabled={disabled}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        <span>
          {selected[0]?.label || (language === 'ar' ? 'اختر' : 'Choose')}
          {selected.length > 1 ? ` +${selected.length - 1}` : ''}
        </span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && !disabled && (
        <PickerPopover
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
          id={id}
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          role="group"
          aria-labelledby={`${id}-label`}
          className="scout-setup__choice-menu"
          focusFirst
        >
          {options.map((item) => (
            <label key={item.value}>
              <input
                type="checkbox"
                checked={values.includes(item.value)}
                onChange={() =>
                  onChange(
                    values.includes(item.value)
                      ? values.filter((v) => v !== item.value)
                      : [...values, item.value],
                  )
                }
              />
              <span>{item.label}</span>
            </label>
          ))}
        </PickerPopover>
      )}
    </div>
  );
}

function CityPicker({
  country,
  states,
  selected,
  onChange,
  disabled,
  copy,
  countryLabel,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [customState, setCustomState] = useState(states[0] || '');
  useEffect(() => {
    if (!states.includes(customState)) setCustomState(states[0] || '');
  }, [states, customState]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    fetch(`/locations/${countryCode(country)}.json`, {
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error('locations');
        return r.json();
      })
      .then(setRows)
      .catch((e) => {
        if (e.name !== 'AbortError') setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [country]);
  const filtered =
    country === US
      ? rows.filter(([, region]) => states.includes(region))
      : rows;
  const options = [
    ...new Map(
      [
        ...filtered.map(([city, region]) => ({
          city,
          state: region,
          country,
        })),
        ...selected,
      ].map((c) => [
        cityKey(c),
        option(
          cityKey(c),
          [c.city, c.state, countryLabel].filter(Boolean).join(', '),
        ),
      ]),
    ).values(),
  ];
  return (
    <div className="scout-setup__cities">
      <p>
        {copy('Choose cities in', 'اختر مدناً في')} {countryLabel}
      </p>
      <SearchPicker
        label={copy('Search cities', 'ابحث عن مدن')}
        options={options}
        values={selected.map(cityKey)}
        onChange={(items) => onChange(items.map((s) => JSON.parse(s)))}
        disabled={disabled || loading}
        placeholder={
          loading
            ? copy('Loading cities…', 'جارٍ تحميل المدن…')
            : copy('Start typing a city', 'ابدأ بكتابة مدينة')
        }
        removeLabel={copy('Remove', 'إزالة')}
      />
      <details>
        <summary>{copy('City not listed?', 'لم تجد مدينتك؟')}</summary>
        <p className="scout-setup__hint">
          {failed
            ? copy(
                'Suggestions could not load. You can enter your city below.',
                'تعذر تحميل الاقتراحات. يمكنك كتابة مدينتك أدناه.',
              )
            : copy(
                'You can also enter a smaller town. Select the suggested “Use” option to confirm.',
                'يمكنك كتابة بلدة أصغر ثم اختيار «استخدم» للتأكيد.',
              )}
        </p>
        {country === US && (
          <label>
            {copy('State', 'الولاية')}
            <select
              value={customState}
              onChange={(e) => setCustomState(e.target.value)}
            >
              {states.map((state) => (
                <option key={state}>{state}</option>
              ))}
            </select>
          </label>
        )}
        <SearchPicker
          label={copy('City name', 'اسم المدينة')}
          options={[]}
          values={[]}
          allowCustom
          onChange={(items) => {
            const city = items[0];
            if (
              city &&
              !selected.some((c) => c.city === city && c.state === customState)
            )
              onChange([
                ...selected,
                { city, state: country === US ? customState : '', country },
              ]);
          }}
          customLabel={copy('Use', 'استخدم')}
          disabled={disabled}
        />
      </details>
      <small>
        <a href="https://www.geonames.org/" target="_blank" rel="noreferrer">
          GeoNames
        </a>{' '}
        · CC BY 4.0
      </small>
    </div>
  );
}

export default function ScoutSetupForm({
  initialAnswers = EMPTY,
  extractedProfile = EMPTY,
  bootstrap = EMPTY,
  messages = [],
  firstName = '',
  cvPending = false,
  cvFailed = false,
  editing = false,
  onSubmit,
  busy = false,
  onBack,
  initialStep = 0,
  detailsOnly = false,
}) {
  const { language } = useLanguage();
  const copy = (en, ar) => (language === 'ar' ? ar : en);
  const [step, setStep] = useState(detailsOnly ? 2 : initialStep);
  const [error, setError] = useState('');
  const [referral, setReferral] = useState('');
  const [replies, setReplies] = useState([]);
  const touched = useRef(new Set());
  const headingRef = useRef(null);
  const [draft, setDraft] = useState(() => ({
    roles: split(initialAnswers.targetDirection),
    seniority: initialAnswers.seniorityPreference || '',
    jobTypes: initialAnswers.jobTypes || ['full_time'],
    workSetup: initialAnswers.workSetup || [],
    residence: initialAnswers.residenceCountry || '',
    countries: split(initialAnswers.targetCountries),
    states: initialAnswers.targetStates || [],
    cities: initialAnswers.targetCityEntries || [],
    authorization: initialAnswers.workAuthorizationCountries || '',
    languages: initialAnswers.professionalLanguages || '',
    salary: initialAnswers.salaryPreference || '',
    dealbreakers: initialAnswers.dealbreakers || '',
  }));
  const set = (key, value) => {
    touched.current.add(key);
    setDraft((d) => ({ ...d, [key]: value }));
    setError('');
  };
  useEffect(() => {
    if (editing) return;
    const cv = extractedProfile.cv || {};
    const roles = extractedProfile.goals?.targetTitles?.length
      ? extractedProfile.goals.targetTitles
      : bootstrap.suggestedTitles || [];
    const seniority = extractedProfile.goals?.seniority?.[0];
    setDraft((d) => ({
      ...d,
      roles:
        !touched.current.has('roles') && roles.length
          ? roles.slice(0, 6)
          : d.roles,
      seniority:
        !touched.current.has('seniority') &&
        [
          'internship',
          'entry_junior',
          'junior_mid',
          'mid_senior',
          'manager_plus',
        ].includes(seniority)
          ? seniority
          : d.seniority,
      residence:
        !touched.current.has('residence') &&
        extractedProfile.locationPreferences?.residenceCountry
          ? extractedProfile.locationPreferences.residenceCountry
          : d.residence,
      languages:
        !touched.current.has('languages') && cv.languages?.length
          ? cv.languages.join('; ')
          : d.languages,
    }));
  }, [extractedProfile, bootstrap, editing]);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);
  const displayNames = useMemo(
    () => new Intl.DisplayNames([language], { type: 'region' }),
    [language],
  );
  const countries = locationOptions.countries.map((c) => ({
    ...option(c.name, language === 'ar' ? displayNames.of(c.code) || c.name : c.name),
    searchTerms: [c.code],
  })).sort((a, b) => a.label.localeCompare(b.label, language));
  const countryLabel = (country) =>
    countries.find((c) => c.value === country)?.label || country;
  const roles = [
    ...new Set([
      ...(extractedProfile.goals?.targetTitles || []),
      ...(bootstrap.suggestedTitles || []),
      ...draft.roles,
    ]),
  ].map((r) => option(r));
  const titles = editing
    ? [
        copy(
          'What should Scout look for?',
          'ما الوظائف التي يبحث عنها المساعد؟',
        ),
        copy('Where would you like to work?', 'أين ترغب في العمل؟'),
        copy('Improve your matches', 'حسّن فرصك المقترحة'),
      ]
    : [
        copy('What should I look for?', 'ما الوظائف التي أبحث عنها لك؟'),
        copy('Where would you like to work?', 'أين ترغب في العمل؟'),
        copy('One last thing', 'سؤال أخير'),
      ];
  const advance = async () => {
    if (
      step === 0 &&
      (!draft.roles.length ||
        draft.roles.length > 6 ||
        draft.roles.some((r) => r.length > 120) ||
        !draft.seniority ||
        !draft.jobTypes.length)
    ) {
      setError(
        copy(
          'Choose 1–6 roles, your desired level and at least one job type.',
          'اختر من وظيفة إلى ست وظائف، والمستوى المطلوب ونوع فرصة واحداً على الأقل.',
        ),
      );
      return;
    }
    if (
      step === 1 &&
      (!draft.residence ||
        !draft.countries.length ||
        !draft.workSetup.some((v) => v !== 'open_to_relocation') ||
        (draft.countries.includes(US) && !draft.states.length))
    ) {
      setError(
        copy(
          'Choose your current country, target countries and work setup. For the US, select at least one state.',
          'اختر بلد إقامتك والبلدان المطلوبة ونظام العمل. للولايات المتحدة، اختر ولاية واحدة على الأقل.',
        ),
      );
      return;
    }
    if (step < 2) {
      if (step === 0)
        ['roles', 'seniority'].forEach((key) => touched.current.add(key));
      if (step === 1) touched.current.add('residence');
      const labels = {
        internship: copy('Internship', 'تدريب'),
        entry_junior: copy('Entry / junior', 'مبتدئ'),
        junior_mid: copy('Junior / mid-level', 'مبتدئ / متوسط'),
        mid_senior: copy('Mid / senior', 'متوسط / متقدم'),
        manager_plus: copy(
          'Lead / manager / executive',
          'قائد / مدير / تنفيذي',
        ),
        full_time: copy('Full-time', 'دوام كامل'),
        part_time: copy('Part-time', 'دوام جزئي'),
        contract: copy('Contract', 'عقد'),
        temporary: copy('Temporary', 'مؤقت'),
        freelance: copy('Freelance', 'عمل حر'),
        remote: copy('Remote', 'عن بُعد'),
        hybrid: copy('Hybrid', 'هجين'),
        onsite: copy('On-site', 'حضوري'),
        on_site: copy('On-site', 'حضوري'),
        open_to_relocation: copy('Open to relocation', 'مستعد للانتقال'),
      };
      const summary =
        step === 0
          ? [
              draft.roles.join(', '),
              labels[draft.seniority],
              draft.jobTypes.map((v) => labels[v] || v).join(', '),
            ].join(' · ')
          : [
              copy('I live in ', 'أقيم في ') + countryLabel(draft.residence),
              draft.countries.map(countryLabel).join(', '),
              ...(draft.countries.includes(US)
                ? [draft.states.join(', ')]
                : []),
              draft.workSetup.map((v) => labels[v] || v).join(', '),
              ...draft.cities.map((c) => c.city),
            ]
              .filter(Boolean)
              .join(' · ');
      setReplies((previous) => [...previous.slice(0, step), summary]);
      setError('');
      setStep(step + 1);
      return;
    }
    if (editing && draft.countries.includes(US) && !draft.states.length) {
      setError(
        copy(
          'Select at least one US state.',
          'اختر ولاية أمريكية واحدة على الأقل.',
        ),
      );
      return;
    }
    if (!editing && !referral) {
      setError(
        copy('Please choose how you heard about us.', 'اختر كيف سمعت عنا.'),
      );
      return;
    }
    try {
      await onSubmit(setupAnswers(draft), referral);
    } catch (e) {
      setError(
        e.message ||
          copy('Could not save. Please try again.', 'تعذر الحفظ. حاول مجدداً.'),
      );
    }
  };
  const cv = extractedProfile.cv || {};
  const experience = cv.currentTitle || cv.pastTitles?.[0];
  const form = (
    <section
      aria-label={editing ? undefined : copy('Your reply', 'إجابتك')}
      className={
        editing
          ? 'scout-setup'
          : 'scout-setup js-onboarding__question js-onboarding__grouped-answer'
      }
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      {editing && (
        <>
          <div className="scout-setup__progress">
            <span>
              {editing ? copy('Edit Scout', 'تعديل المساعد') : 'Sigmund'}
            </span>
            <span dir="ltr">{detailsOnly ? '1 / 1' : `${step + 1} / 3`}</span>
          </div>
          <div className="scout-setup__track">
            <span
              style={{
                width: `${detailsOnly ? 100 : ((step + 1) / 3) * 100}%`,
              }}
            />
          </div>
          <h1 tabIndex={-1} ref={headingRef}>
            {titles[step]}
          </h1>
        </>
      )}
      <div
        className={
          editing
            ? 'scout-setup__fields'
            : `js-onboarding__grouped-fields is-group-${step}`
        }
      >
        {step === 0 && (
          <>
            {editing && (
              <p className="scout-setup__intro">
                {experience
                  ? copy(
                      `Your CV shows experience as ${experience}. Keep the suggested roles, or choose a new direction.`,
                      `تُظهر سيرتك خبرة في ${experience}. أكّد الوظائف المقترحة أو اختر اتجاهاً جديداً.`,
                    )
                  : bootstrap.summaryHook ||
                    copy(
                      'Tell me what you want next. You can change these preferences later.',
                      'أخبرني بما تطمح إليه. يمكنك تعديل هذه التفضيلات لاحقاً.',
                    )}
              </p>
            )}
            {cvPending && editing && (
              <p role="status" className="scout-setup__hint">
                {copy(
                  'Reading your CV—you can start now. Your edits will be kept.',
                  'نقرأ سيرتك الآن—يمكنك البدء وسنحتفظ بتعديلاتك.',
                )}
              </p>
            )}
            {cvFailed && editing && (
              <p role="status" className="scout-setup__hint">
                {copy(
                  'CV suggestions are unavailable. Choose your preferences below; your saved CV will be retried during setup.',
                  'اقتراحات السيرة غير متاحة. اختر تفضيلاتك أدناه وسنحاول قراءة السيرة المحفوظة أثناء الإعداد.',
                )}
              </p>
            )}
            <SearchPicker
              label={copy('Target roles', 'الوظائف المطلوبة')}
              options={roles}
              values={draft.roles}
              onChange={(v) => set('roles', v)}
              allowCustom
              disabled={busy}
              placeholder={copy(
                'Search or add a role',
                'اكتب وظيفة ثم اخترها',
              )}
              customLabel={copy('Use', 'استخدم')}
              removeLabel={copy('Remove', 'إزالة')}
            />
            <label>
              {copy('Desired level', 'المستوى المطلوب')}
              <select
                value={draft.seniority}
                disabled={busy}
                onChange={(e) => set('seniority', e.target.value)}
              >
                <option value="">
                  {copy(
                    'Choose your next level',
                    'اختر المستوى الذي تطمح إليه',
                  )}
                </option>
                {[
                  ['internship', 'Internship / student', 'تدريب / طالب'],
                  ['entry_junior', 'Entry / junior', 'مبتدئ'],
                  ['junior_mid', 'Junior / mid-level', 'مبتدئ / متوسط'],
                  ['mid_senior', 'Mid / senior', 'متوسط / متقدم'],
                  [
                    'manager_plus',
                    'Lead / manager / executive',
                    'قائد / مدير / تنفيذي',
                  ],
                ].map(([v, en, ar]) => (
                  <option key={v} value={v}>
                    {copy(en, ar)}
                  </option>
                ))}
              </select>
            </label>
            <Choices
              dropdown={!editing}
              label={copy('Opportunity types', 'أنواع الفرص')}
              options={[
                ['full_time', 'Full-time', 'دوام كامل'],
                ['part_time', 'Part-time', 'دوام جزئي'],
                ['contract', 'Contract', 'عقد'],
                ['internship', 'Internship', 'تدريب'],
                ['temporary', 'Temporary', 'مؤقت'],
                ['freelance', 'Freelance', 'عمل حر'],
              ].map(([v, en, ar]) => option(v, copy(en, ar)))}
              values={draft.jobTypes}
              onChange={(v) => set('jobTypes', v)}
              disabled={busy}
            />
          </>
        )}
        {step === 1 && (
          <>
            <SearchPicker
              label={copy(
                'Current country',
                'بلد الإقامة الحالي',
              )}
              options={countries}
              values={draft.residence ? [draft.residence] : []}
              multiple={false}
              onChange={(v) => set('residence', v[0] || '')}
              disabled={busy}
              placeholder={copy('Country', 'البلد')}
              removeLabel={copy('Remove', 'إزالة')}
            />
            <SearchPicker
              label={copy(
                editing
                  ? 'Which employer countries should Scout search?'
                  : 'Countries to search',
                'في أي بلدان تبحث عن أصحاب العمل؟',
              )}
              options={countries}
              values={draft.countries}
              onChange={(v) => {
                set('countries', v);
                set(
                  'cities',
                  draft.cities.filter((c) => v.includes(c.country)),
                );
                if (!v.includes(US)) set('states', []);
              }}
              disabled={busy}
              placeholder={copy('Add country', 'أضف بلداً')}
              removeLabel={copy('Remove', 'إزالة')}
            />
            {draft.countries.includes(US) && (
              <div className="scout-setup__us">
                <SearchPicker
                  label={copy(
                    'US states · required',
                    'الولايات الأمريكية · مطلوب',
                  )}
                  options={locationOptions.states.map((s) => option(s))}
                  values={draft.states}
                  onChange={(v) => {
                    set('states', v);
                    set(
                      'cities',
                      draft.cities.filter(
                        (c) => c.country !== US || v.includes(c.state),
                      ),
                    );
                  }}
                  disabled={busy}
                  placeholder={copy('Search states', 'ابحث عن ولاية')}
                  removeLabel={copy('Remove', 'إزالة')}
                />
                <p className="scout-setup__hint">
                  {copy(
                    'Search is limited to your selected states.',
                    'نبحث في الولايات التي تختارها فقط.',
                  )}
                </p>
              </div>
            )}
            <Choices
              label={copy('Work setup', 'نظام العمل')}
              options={[
                ['remote', 'Remote', 'عن بُعد'],
                ['hybrid', 'Hybrid', 'هجين'],
                ['on_site', 'On-site', 'في مقر العمل'],
              ].map(([v, en, ar]) => option(v, copy(en, ar)))}
              values={draft.workSetup}
              onChange={(v) => set('workSetup', v)}
              disabled={busy}
            />
            <label className="scout-setup__check">
              <input
                type="checkbox"
                disabled={busy}
                checked={draft.workSetup.includes('open_to_relocation')}
                onChange={(e) =>
                  set(
                    'workSetup',
                    e.target.checked
                      ? [...draft.workSetup, 'open_to_relocation']
                      : draft.workSetup.filter(
                          (v) => v !== 'open_to_relocation',
                        ),
                  )
                }
              />
              {copy('I’m open to relocation', 'أنا مستعد للانتقال')}
            </label>
            <details>
              <summary>
                {copy(
                  'Choose specific cities (optional)',
                  'اختيار مدن محددة (اختياري)',
                )}
              </summary>
              {draft.countries.map((country) => (
                <div key={country} className="scout-setup__country">
                  <p className="scout-setup__hint">
                    {country === US
                      ? copy(
                          'Each selected state includes all its cities unless you choose cities within that state.',
                          'تشمل كل ولاية محددة جميع مدنها ما لم تختر مدناً ضمنها.',
                        )
                      : `${copy('All cities unless you choose below:', 'كل المدن ما لم تحدد أدناه:')} ${countryLabel(country)}`}
                  </p>
                  {draft.cities.some((c) => c.country === country) && (
                    <button
                      className="scout-setup__chip"
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        set(
                          'cities',
                          draft.cities.filter((c) => c.country !== country),
                        )
                      }
                    >
                      {copy('Reset to all cities', 'العودة إلى كل المدن')}
                    </button>
                  )}
                  {(country !== US || draft.states.length > 0) && (
                    <CityPicker
                      country={country}
                      countryLabel={countryLabel(country)}
                      states={draft.states}
                      selected={draft.cities.filter(
                        (c) => c.country === country,
                      )}
                      onChange={(v) =>
                        set('cities', [
                          ...draft.cities.filter((c) => c.country !== country),
                          ...v,
                        ])
                      }
                      disabled={busy}
                      copy={copy}
                    />
                  )}
                </div>
              ))}
            </details>
            <p className="scout-setup__hint">
              {copy(
                'All cities are included unless you choose specific ones.',
                'ما لم تحدد مدناً، نبحث في كل مدن البلدان والولايات المختارة. يمكنك إضافة تصريح العمل لاحقاً.',
              )}
            </p>
          </>
        )}
        {step === 2 && !editing && (
          <>
            <div
              className="scout-setup__referrals"
              role="radiogroup"
              aria-label={copy('How did you hear about us?', 'كيف سمعت عنا؟')}
            >
              {[
                ['friend_colleague', 'Friend or colleague', 'صديق أو زميل'],
                ['linkedin', 'LinkedIn', 'لينكدإن'],
                ['google_search', 'Google or search', 'جوجل أو البحث'],
                [
                  'university_community',
                  'University or community',
                  'جامعة أو مجتمع',
                ],
                ['social_media', 'Social media', 'وسائل التواصل'],
                ['event', 'Event', 'فعالية'],
                ['other', 'Other', 'أخرى'],
              ].map(([v, en, ar]) => (
                <label key={v}>
                  <input
                    type="radio"
                    name="referral"
                    value={v}
                    checked={referral === v}
                    disabled={busy}
                    onChange={() => setReferral(v)}
                  />
                  {copy(en, ar)}
                </label>
              ))}
            </div>
          </>
        )}
        {step === 2 && editing && (
          <>
            {detailsOnly &&
              draft.countries.includes(US) &&
              !(initialAnswers.targetStates || []).length && (
                <div className="scout-setup__us">
                  <SearchPicker
                    label={copy(
                      'US states · required',
                      'الولايات الأمريكية · مطلوب',
                    )}
                    options={locationOptions.states.map((state) =>
                      option(state),
                    )}
                    values={draft.states}
                    onChange={(value) => set('states', value)}
                    disabled={busy}
                    placeholder={copy('Search states', 'ابحث عن ولاية')}
                    removeLabel={copy('Remove', 'إزالة')}
                  />
                  <p className="scout-setup__hint">
                    {copy(
                      'Choose states to continue collecting US opportunities.',
                      'اختر الولايات لمواصلة جمع الفرص الأمريكية.',
                    )}
                  </p>
                </div>
              )}
            <p>
              {copy(
                'Optional details help Scout assess your matches. Leave anything you’re not ready to answer.',
                'تفاصيل اختيارية تساعد المساعد على تقييم الفرص. يمكنك ترك ما لا ترغب بالإجابة عنه.',
              )}
            </p>
            <SearchPicker
              label={copy(
                'Countries where you can work without employer sponsorship',
                'بلدان تستطيع العمل فيها دون كفالة صاحب العمل',
              )}
              options={countries}
              values={
                draft.authorization === 'None' ? [] : split(draft.authorization)
              }
              onChange={(v) => set('authorization', v.join('; '))}
              disabled={busy}
              placeholder={copy('Not answered yet', 'لم تُجب بعد')}
              removeLabel={copy('Remove', 'إزالة')}
            />
            <label className="scout-setup__check">
              <input
                type="checkbox"
                checked={draft.authorization === 'None'}
                disabled={busy}
                onChange={(e) =>
                  set('authorization', e.target.checked ? 'None' : '')
                }
              />
              {copy(
                'I would need employer sponsorship in all my target countries',
                'أحتاج إلى كفالة صاحب العمل في جميع البلدان المطلوبة',
              )}
            </label>
            <SearchPicker
              label={copy('Professional languages', 'اللغات المهنية')}
              options={[
                'English',
                'Arabic',
                'French',
                'Spanish',
                'German',
                'Portuguese',
                'Mandarin',
                'Hindi',
              ].map((v) => option(v))}
              values={split(draft.languages)}
              onChange={(v) => set('languages', v.join('; '))}
              allowCustom
              customLabel={copy('Use', 'استخدم')}
              disabled={busy}
              removeLabel={copy('Remove', 'إزالة')}
            />
            <SalaryPicker
              value={draft.salary}
              onChange={(value) => set('salary', value)}
              disabled={busy}
              copy={copy}
            />
            <SearchPicker
              label={copy(
                'Conditions to exclude (optional)',
                'شروط تريد استبعادها (اختياري)',
              )}
              options={[
                option(
                  'night shift',
                  copy('Night shifts', 'المناوبات الليلية'),
                ),
                option(
                  'commission only',
                  copy('Commission-only pay', 'أجر بالعمولة فقط'),
                ),
                option(
                  'unpaid internship',
                  copy('Unpaid internships', 'التدريب غير المدفوع'),
                ),
              ]}
              values={
                draft.dealbreakers === 'None' ? [] : split(draft.dealbreakers)
              }
              onChange={(values) => set('dealbreakers', values.join('; '))}
              allowCustom
              customLabel={copy('Use', 'استخدم')}
              placeholder={copy(
                'Choose or type a condition',
                'اختر شرطاً أو اكتبه',
              )}
              disabled={busy}
              removeLabel={copy('Remove', 'إزالة')}
            />
          </>
        )}
      </div>
      {error && (
        <p role="alert" className="scout-setup__error">
          {error}
        </p>
      )}
      <div
        className={
          editing ? 'scout-setup__actions' : 'js-onboarding__reply-actions'
        }
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setError('');
            if (step > 0 && !detailsOnly) setStep(step - 1);
            else onBack?.();
          }}
        >
          {copy('Back', 'رجوع')}
        </button>
        <button
          type="button"
          className={editing ? 'scout-setup__primary' : 'js-onboarding__send'}
          aria-label={
            editing
              ? undefined
              : step < 2
                ? copy('Continue', 'متابعة')
                : copy('Proceed', 'متابعة')
          }
          title={
            editing
              ? undefined
              : step < 2
                ? copy('Continue', 'متابعة')
                : copy('Proceed', 'متابعة')
          }
          disabled={busy}
          onClick={advance}
        >
          {!editing ? (
            <FiSend aria-hidden="true" />
          ) : busy ? (
            copy('Saving…', 'جارٍ الحفظ…')
          ) : step < 2 ? (
            copy('Continue', 'متابعة')
          ) : editing ? (
            copy('Save and refresh matches', 'حفظ وتحديث الفرص')
          ) : (
            copy('Proceed', 'متابعة')
          )}
        </button>
      </div>
    </section>
  );
  if (editing) return form;
  const welcome = messages.filter(
    (message) => message.role === 'assistant' && message.content,
  );
  const greeting =
    welcome[0]?.content ||
    copy(
      `Hey ${firstName || 'there'} — I'm Sigmund. I'm here to help you find stronger opportunities and make smarter career moves.`,
      `مرحباً ${firstName || 'بك'} — أنا سيغموند. سأساعدك في العثور على فرص أقوى واتخاذ خطوات مهنية أذكى.`,
    );
  const introduction = welcome.slice(1).map((message) => message.content);
  if (!introduction.length) {
    const intro =
      bootstrap.summaryHook ||
      (experience
        ? copy(
            `Your CV shows experience as ${experience}. Keep the suggested roles, or choose a new direction.`,
            `تُظهر سيرتك خبرة في ${experience}. أكّد الوظائف المقترحة أو اختر اتجاهاً جديداً.`,
          )
        : '');
    if (intro) introduction.push(intro);
  }
  const prompts = [
    copy(
      'What would you like to do next? Choose your roles, level and job types below.',
      'ما العمل الذي تطمح إليه؟ اختر الوظائف والمستوى وأنواع الفرص أدناه.',
    ),
    copy(
      'Got it. Where would you like to work, and what work setup suits you?',
      'فهمت. أين ترغب في العمل، وما نظام العمل الذي يناسبك؟',
    ),
    copy(
      'Thanks. One last thing — how did you hear about JOBSEEQR?',
      'شكراً. سؤال أخير — كيف سمعت عن JOBSEEQR؟',
    ),
  ];
  const chatMessages = [
    { id: 'greeting', role: 'assistant', content: greeting },
    ...introduction.map((content) => ({
      id: `intro-${content}`,
      role: 'assistant',
      content,
    })),
  ];
  for (let index = 0; index <= step; index += 1) {
    chatMessages.push({
      id: `question-${index}`,
      role: 'assistant',
      content: prompts[index],
    });
    if (index < step && replies[index])
      chatMessages.push({
        id: `reply-${index}`,
        role: 'user',
        content: replies[index],
      });
  }
  return (
    <OnboardingChat messages={chatMessages} step={step}>
      {form}
    </OnboardingChat>
  );
}
